import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core/theme';
import { Center } from '@astryxdesign/core/Center';
import { Spinner } from '@astryxdesign/core/Spinner';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Button } from '@astryxdesign/core/Button';
import { publicApi, ApiError, type PublicApi } from '@/lib/api';
import type { Tenant } from '@/lib/types';
import { tenantTheme } from '@/theme/tenant-theme';

interface TenantCtx {
  slug: string;
  tenant: Tenant;
  api: PublicApi;
}

const Ctx = createContext<TenantCtx | null>(null);

export function useTenant() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTenant outside TenantProvider');
  return v;
}

export const tenantQueryKey = (slug: string) => ['tenant', slug] as const;

export function TenantProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const api = publicApi(slug);
  const q = useQuery({ queryKey: tenantQueryKey(slug), queryFn: api.tenant, staleTime: 5 * 60_000 });

  useEffect(() => {
    if (q.data) document.title = q.data.name;
  }, [q.data]);

  if (q.isPending) {
    return (
      <Center minHeight="100dvh">
        <Spinner size="lg" label="Загрузка студии" />
      </Center>
    );
  }
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.code === 'tenant_not_found';
    return (
      <Center minHeight="100dvh" padding={6}>
        <EmptyState
          title={notFound ? 'Студия не найдена' : 'Не удалось загрузить студию'}
          description={notFound ? 'Проверьте ссылку.' : q.error.message}
          actions={notFound ? undefined : <Button label="Повторить" variant="primary" onClick={() => q.refetch()} />}
        />
      </Center>
    );
  }

  return (
    <Ctx.Provider value={{ slug, tenant: q.data, api }}>
      <Theme theme={tenantTheme(slug, q.data.accent)} mode="dark">
        {children}
      </Theme>
    </Ctx.Provider>
  );
}
